import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowLeft, Users, Play, Square, BarChart3, Settings, CheckCircle2, Clock, Loader2, Eye, RotateCcw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import {
  getConsulta, getCensusStats, activateConsulta, closeConsulta, updateConsulta, uploadImage, resetScrutiny
} from "../lib/api";
import { formatLocalDateTime } from "../lib/dateUtils";
import { API_URL } from "../lib/config";
import { toast } from "sonner";

import { StatusBadge } from "../components/consulta/StatusBadge";
import { VotingLinkSection } from "../components/consulta/VotingLinkSection";
import { InfoSection } from "../components/consulta/InfoSection";
import { RestrictionsSection } from "../components/consulta/RestrictionsSection";
import { AdvancedOptionsSection } from "../components/consulta/AdvancedOptionsSection";
import { ProposalsSection } from "../components/consulta/ProposalsSection";

const defaultForm = {
  title: "", description: "", eligibility: "",
  startDate: "", startTime: "09:00", endDate: "", endTime: "21:00",
  restrictionType: "options", minOptions: 1, maxOptions: 1, maxTotalAmount: "",
  requireRegistration: false, enablePrivateAudit: false, enableVotMaAlcada: false,
  enableAudit: false, enableVoterTracking: false, enableStatsSex: true, enableStatsAge: true,
};

function buildFormFromConsulta(c) {
  const parseDT = (iso, fallbackTime) => {
    if (!iso) return { date: "", time: fallbackTime };
    const s = String(iso);
    const [datePart, timePart] = s.split("T");
    return { date: datePart, time: timePart ? timePart.slice(0, 5) : fallbackTime };
  };
  const start = parseDT(c.start_date, "00:00");
  const end = parseDT(c.end_date, "00:00");
  return {
    title: c.title || "",
    description: c.description || "",
    eligibility: c.eligibility || "",
    imageUrl: c.image_url || null,
    imageUrls: c.image_urls || [],
    startDate: start.date, startTime: start.time,
    endDate: end.date, endTime: end.time,
    restrictionType: c.restriction_type || "options",
    minOptions: c.min_options || 1,
    maxOptions: c.max_options || 1,
    maxTotalAmount: c.max_total_amount || "",
    requireRegistration: c.require_registration ?? false,
    enablePrivateAudit: c.enable_private_audit ?? false,
    enableVotMaAlcada: c.enable_vot_ma_alcada ?? false,
    enableAudit: c.enable_audit ?? false,
    enableVoterTracking: c.enable_voter_tracking ?? false,
    enableStatsSex: c.enable_stats_sex ?? true,
    enableStatsAge: c.enable_stats_age ?? true,
  };
}

function buildOptionsFromConsulta(c) {
  return c.options.map(opt =>
    typeof opt === "string"
      ? { title: opt, description: "", image_url: null, image_urls: [], amount: null }
      : { ...opt, image_urls: opt.image_urls || [] }
  );
}

export default function ConsultaDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [consulta, setConsulta] = useState(null);
  const [censusStats, setCensusStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [editedOptions, setEditedOptions] = useState([]);
  const fileInputRefs = useRef({});

  const onFieldChange = (key, value, extra) => setForm(prev => ({ ...prev, [key]: value, ...(extra || {}) }));

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [consultaRes, censusRes] = await Promise.all([
        getConsulta(id),
        getCensusStats(id).catch(() => ({ data: { total: 0, registered: 0, voted: 0, participation: 0 } }))
      ]);
      setConsulta(consultaRes.data);
      setCensusStats(censusRes.data);
      setForm(buildFormFromConsulta(consultaRes.data));
      setEditedOptions(buildOptionsFromConsulta(consultaRes.data));
    } catch {
      toast.error(t("errors.loadingData"));
      navigate("/consultes");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (censusStats?.total === 0) { toast.error(t("consultaDetail.loadCensusFirst")); return; }
    try {
      await activateConsulta(id);
      toast.success(t("consultaDetail.activationSent"));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("consultaDetail.activateError"));
    }
  };

  const handleClose = async () => {
    if (!window.confirm(t("consultaDetail.closeConfirm"))) return;
    try {
      const response = await closeConsulta(id);
      toast.success(response.data?.message || t("consultaDetail.requestSent"));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("consultaDetail.closeError"));
    }
  };

  const handleResetScrutiny = async () => {
    if (!window.confirm(t("scrutiny.resetConfirm"))) return;
    try {
      await resetScrutiny(id);
      toast.success(t("scrutiny.resetSuccess"));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errors.generic"));
    }
  };

  const handleOptionChange = (index, field, value) => {
    const next = [...editedOptions];
    next[index] = { ...next[index], [field]: value };
    setEditedOptions(next);
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) { toast.error(t("consultaDetail.formatNotAllowed")); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error(t("consultaDetail.fileTooLarge")); return; }
    const currentUrls = editedOptions[index].image_urls || [];
    if (currentUrls.length >= 6) { toast.error(t("consultaDetail.maxImagesReached")); return; }
    setUploadingIndex(index);
    try {
      const response = await uploadImage(file);
      const newUrl = response.data.url;
      const updatedUrls = [...currentUrls, newUrl];
      const next = [...editedOptions];
      next[index] = { ...next[index], image_urls: updatedUrls, image_url: updatedUrls[0] };
      setEditedOptions(next);
      toast.success(t("consultaDetail.imageUploaded"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("consultaDetail.imageUploadError"));
    } finally {
      setUploadingIndex(null);
    }
  };

  const removeOptionImage = (optionIndex, imgIdx) => {
    const currentUrls = [...(editedOptions[optionIndex].image_urls || [])];
    currentUrls.splice(imgIdx, 1);
    const next = [...editedOptions];
    next[optionIndex] = { ...next[optionIndex], image_urls: currentUrls, image_url: currentUrls[0] || null };
    setEditedOptions(next);
  };

  const addOption = () => setEditedOptions([...editedOptions, { title: "", description: "", image_url: null, image_urls: [] }]);

  const removeOption = (index) => {
    if (editedOptions.length > 2) setEditedOptions(editedOptions.filter((_, i) => i !== index));
    else toast.error(t("consultaDetail.minOptions"));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error(t("consultaDetail.titleRequired")); return; }
    const validOptions = editedOptions.filter(o => o.title.trim());
    if (validOptions.length < 2) { toast.error(t("consultaDetail.minOptionsWithTitle")); return; }
    if (!form.startDate || !form.endDate) { toast.error(t("consultaDetail.datesRequired")); return; }
    const startDT = new Date(`${form.startDate}T${form.startTime}`);
    const endDT = new Date(`${form.endDate}T${form.endTime}`);
    if (startDT >= endDT) { toast.error(t("consultaDetail.endAfterStart")); return; }

    setSaving(true);
    try {
      const pad = (n) => String(n).padStart(2, "0");
      const toLocalISO = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:00`;
      await updateConsulta(id, {
        title: form.title.trim(),
        description: form.description.trim(),
        eligibility: form.eligibility.trim() || null,
        image_url: form.imageUrl || null,
        image_urls: form.imageUrls?.length ? form.imageUrls : null,
        start_date: toLocalISO(startDT),
        end_date: toLocalISO(endDT),
        options: editedOptions.filter(o => o.title.trim()).map(o => ({
          title: o.title.trim(),
          description: o.description?.trim() || null,
          image_url: o.image_url || null,
          image_urls: o.image_urls?.length ? o.image_urls : null,
          amount: o.amount ? parseFloat(o.amount) : null,
        })),
        restriction_type: form.restrictionType,
        min_options: parseInt(form.minOptions) || 1,
        max_options: parseInt(form.maxOptions) || 1,
        max_total_amount: form.maxTotalAmount ? parseInt(form.maxTotalAmount) : null,
        require_registration: form.requireRegistration,
        enable_private_audit: form.enablePrivateAudit,
        enable_vot_ma_alcada: form.enableVotMaAlcada,
        enable_audit: form.enableAudit,
        enable_voter_tracking: form.enableVoterTracking,
        enable_stats_sex: form.enableStatsSex,
        enable_stats_age: form.enableStatsAge,
      });
      toast.success(t("consultaDetail.updated"));
      setEditing(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("consultaDetail.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setForm(buildFormFromConsulta(consulta));
    setEditedOptions(buildOptionsFromConsulta(consulta));
    setEditing(false);
  };

  const copyVotingUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}/vote/${id}`);
    setCopied(true);
    toast.success(t("common.linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const isWithinVotingDates = () => {
    if (!consulta?.start_date || !consulta?.end_date) return false;
    const now = new Date();
    return now >= new Date(consulta.start_date) && now <= new Date(consulta.end_date);
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-screen"><div className="spinner" /></div></Layout>;
  }
  if (!consulta) return null;

  return (
    <Layout>
      <div className="content-container max-w-4xl" data-testid="consulta-detail-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Button variant="ghost" onClick={() => navigate("/consultes")} className="mb-4" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('common.back')}
          </Button>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-heading font-bold text-slate-900">
                {editing ? (form.title || consulta.title) : consulta.title}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={consulta.status} isVoting={consulta.status === "published" && isWithinVotingDates()} scrutinyRequestStatus={consulta.scrutiny_request_status} />
                {!editing && (
                  <span className="text-sm text-slate-500">
                    {formatLocalDateTime(consulta.start_date, consulta.entity_timezone)} - {formatLocalDateTime(consulta.end_date, consulta.entity_timezone)}
                  </span>
                )}
              </div>
            </div>
            <HeaderActions
              status={consulta.status}
              id={id}
              scrutinyRequestStatus={consulta.scrutiny_request_status}
              onActivate={handleActivate}
              onClose={handleClose}
              onResetScrutiny={handleResetScrutiny}
            />
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} iconBg="bg-blue-100" iconColor="text-blue-600" value={censusStats?.total || 0} label={t("consultaDetail.totalCensus")} />
          <StatCard icon={CheckCircle2} iconBg="bg-green-100" iconColor="text-green-600" value={censusStats?.voted || 0} label={t("consultaDetail.votesReceived")} />
          <StatCard icon={BarChart3} iconBg="bg-purple-100" iconColor="text-purple-600" value={`${censusStats?.participation?.toFixed(1) || 0}%`} label={t("consultaDetail.participation")} />
          <Link to={`/consultes/${id}/census`} className="block">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Settings className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{consulta.status === "draft" ? t("consultaDetail.manageCensus") : t("consultaDetail.viewCensus")}</p>
                  <p className="text-xs text-slate-500">{consulta.status === "draft" ? t("consultaDetail.addOrModify") : t("consultaDetail.readOnly")}</p>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Content sections */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-6">
          {["published", "closed", "pending_blockchain", "scrutinized"].includes(consulta.status) && (
            <VotingLinkSection consulta={consulta} id={id} copied={copied} onCopy={copyVotingUrl} />
          )}

          <InfoSection consulta={consulta} editing={editing} onStartEdit={() => setEditing(true)} form={form} onFieldChange={onFieldChange} saving={saving} onSave={handleSave} onCancel={handleCancelEdit} />
          <RestrictionsSection consulta={consulta} editing={editing} form={form} onFieldChange={onFieldChange} />
          <AdvancedOptionsSection consulta={consulta} editing={editing} form={form} onFieldChange={onFieldChange} />
          <ProposalsSection
            consulta={consulta} editing={editing} options={editedOptions}
            onOptionChange={handleOptionChange} onAddOption={addOption} onRemoveOption={removeOption}
            onImageUpload={handleImageUpload} onRemoveImage={removeOptionImage}
            uploadingIndex={uploadingIndex} fileInputRefs={fileInputRefs}
            saving={saving} onSave={handleSave} onCancel={handleCancelEdit}
          />

          {consulta.status === "pending_blockchain" && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h2 className="text-lg font-heading font-semibold text-orange-900 mb-2">{t('status.pendingBlockchain')}</h2>
              <p className="text-sm text-orange-700">{t('consultaDetail.pendingBlockchainDesc')}</p>
            </div>
          )}

          {consulta.status === "closed" && !consulta.scrutiny_request_status && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h2 className="text-lg font-heading font-semibold text-amber-900 mb-2">{t('consultaDetail.consultaClosed')}</h2>
              <p className="text-sm text-amber-700 mb-4">{t('consultaDetail.consultaClosedDesc')}</p>
              <Link to={`/consultes/${id}/scrutiny`}>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <BarChart3 className="h-4 w-4 mr-2" />{t('consultaDetail.goScrutiny')}
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
}

function HeaderActions({ status, id, scrutinyRequestStatus, onActivate, onClose, onResetScrutiny }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2">
      {status === "draft" && (
        <Button onClick={onActivate} className="bg-green-600 hover:bg-green-700" data-testid="activate-btn">
          <Play className="h-4 w-4 mr-2" />{t("consultaDetail.publish")}
        </Button>
      )}
      {status === "pending_publication" && (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-50 text-orange-700 text-sm font-medium border border-orange-200">
          <Clock className="h-4 w-4" />{t("consultaDetail.pendingAdminApproval")}
        </span>
      )}
      {status === "published" && (
        <Button onClick={onClose} variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="close-btn">
          <Square className="h-4 w-4 mr-2" />{t("consultaDetail.close")}
        </Button>
      )}
      {status === "pending_closure" && (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-medium border border-red-200">
          <Clock className="h-4 w-4" />{t("consultaDetail.pendingClosureAuth")}
        </span>
      )}
      {status === "closed" && !scrutinyRequestStatus && (
        <Link to={`/consultes/${id}/scrutiny`}>
          <Button className="bg-slate-900 hover:bg-slate-800" data-testid="scrutiny-btn">
            <BarChart3 className="h-4 w-4 mr-2" />{t("consultaDetail.scrutiny")}
          </Button>
        </Link>
      )}
      {status === "scrutinized" && (
        <>
          <Button onClick={onResetScrutiny} variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50" data-testid="reset-scrutiny-btn">
            <RotateCcw className="h-4 w-4 mr-2" />{t("scrutiny.resetScrutiny")}
          </Button>
          <Link to={`/consultes/${id}/results`}>
            <Button className="bg-purple-600 hover:bg-purple-700" data-testid="view-results-btn">
              <Eye className="h-4 w-4 mr-2" />{t("scrutiny.viewResults")}
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}
